import React, { useEffect, useMemo, useState } from "react";
import { api, formatEUR } from "@/lib/api";
import { Dog, X, Search, User, Phone, Cake, Camera, PawPrint } from "lucide-react";

const STATUS_BADGES = {
  scheduled: { bg: "#3B82F6", text: "Prenotato" },
  confirmed: { bg: "#8B5CF6", text: "Confermato" },
  in_progress: { bg: "#F59E0B", text: "In corso" },
  completed: { bg: "#2F9E77", text: "Completato" },
  cancelled: { bg: "#94A3B8", text: "Annullato" },
  no_show: { bg: "#DC2626", text: "No-show" },
};

const PHOTO_TYPES = { before: "Prima", after: "Dopo", front: "Fronte", side: "Lato", back: "Retro" };

const fmtDT = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" }) +
      " · " + d.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
};
const fmtD = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return iso; }
};

export const PetsListModal = ({ onClose, onSelect }) => {
  const [pets, setPets] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/pets").then((r) => setPets(Array.isArray(r.data) ? r.data : [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return pets;
    return (Array.isArray(pets) ? pets : []).filter((p) =>
      [p.pet_name, p.pet_breed, p.owner_name, p.owner_phone].some((v) => (v || "").toLowerCase().includes(s))
    );
  }, [pets, q]);

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50" onClick={onClose}>
      <div data-testid="pets-list-modal" className="bg-white rounded-md w-full max-w-2xl max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 border-b border-velta-border flex items-center justify-between">
          <h3 className="font-heading text-2xl font-semibold flex items-center gap-2">
            <PawPrint className="w-6 h-6" /> Animali ({pets.length})
          </h3>
          <button onClick={onClose} data-testid="pets-list-close" className="text-velta-mute hover:text-velta-ink"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-4 border-b border-velta-border">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-velta-mute" />
            <input
              data-testid="pets-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Cerca per nome, razza, proprietario o telefono…"
              className="w-full h-11 pl-9 pr-3 border border-velta-border rounded-md outline-none focus:border-velta-ink"
            />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          {loading && <p className="text-sm text-velta-mute">Caricamento…</p>}
          {!loading && filtered.length === 0 && (
            <p className="text-sm text-velta-mute" data-testid="pets-empty">Nessun animale trovato. Gli animali compaiono qui dopo il primo appuntamento.</p>
          )}
          {(Array.isArray(filtered) ? filtered : []).map((p, i) => (
            <button
              key={`${p.pet_name}-${p.owner_phone}-${i}`}
              data-testid={`pet-row-${i}`}
              onClick={() => onSelect(p)}
              className="w-full text-left border border-velta-border rounded-md p-3 hover:border-velta-ink transition flex items-center gap-4"
            >
              <div className="h-10 w-10 rounded-full bg-velta-bg flex items-center justify-center shrink-0">
                <Dog className="w-5 h-5 text-velta-ink" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-heading font-semibold flex items-center gap-2">
                  {p.pet_name}
                  {p.pet_breed && <span className="text-xs font-normal text-velta-mute">· {p.pet_breed}</span>}
                </div>
                <div className="text-[11px] text-velta-mute flex items-center gap-1.5 mt-0.5">
                  <User className="w-3 h-3" /> {p.owner_name || "—"}
                  {p.owner_phone && <><span>·</span><Phone className="w-3 h-3" /> {p.owner_phone}</>}
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="text-sm font-mono font-bold">{p.visits} visite</div>
                <div className="text-[11px] text-velta-mute">ultima: {fmtD(p.last_visit)}</div>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export const PetHistoryModal = ({ petName, ownerPhone, onClose }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams({ pet_name: petName });
    if (ownerPhone) params.set("owner_phone", ownerPhone);
    api.get(`/pets/history?${params.toString()}`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "Errore caricamento storico"));
  }, [petName, ownerPhone]);

  const openPhoto = async (aid, pid) => {
    try {
      const r = await api.get(`/appointments/${aid}/photos/${pid}`);
      setLightbox(r.data);
    } catch { /* foto non disponibile */ }
  };

  const pet = data?.pet;
  const stats = data?.stats;

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50" onClick={onClose}>
      <div data-testid="pet-history-modal" className="bg-white rounded-md w-full max-w-2xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 border-b border-velta-border flex items-start justify-between">
          <div>
            <h3 className="font-heading text-2xl font-semibold flex items-center gap-2" data-testid="pet-history-name">
              <Dog className="w-6 h-6" /> {pet?.pet_name || petName}
              {pet?.pet_breed && <span className="text-base font-normal text-velta-mute">· {pet.pet_breed}</span>}
            </h3>
            <div className="text-xs text-velta-mute flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5">
              {pet?.pet_type && <span className="capitalize">{pet.pet_type}</span>}
              {pet?.pet_birth_date && <span className="inline-flex items-center gap-1"><Cake className="w-3 h-3 text-[#EC4899]" /> {fmtD(pet.pet_birth_date)}</span>}
              {pet?.owner_name && <span className="inline-flex items-center gap-1"><User className="w-3 h-3" /> {pet.owner_name}</span>}
              {pet?.owner_phone && <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" /> {pet.owner_phone}</span>}
            </div>
            {pet?.pet_notes && <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 mt-2 inline-block">⚠ {pet.pet_notes}</div>}
            {pet?.pet_allergies && <div className="text-[11px] text-red-800 bg-red-50 border border-red-200 rounded px-2 py-1 mt-2 ml-1 inline-block">Allergie: {pet.pet_allergies}</div>}
            {pet?.pet_cut_pref && <div className="text-[11px] text-velta-ink bg-velta-bg border border-velta-border rounded px-2 py-1 mt-2 ml-1 inline-block">Taglio: {pet.pet_cut_pref}</div>}
            {pet?.pet_behavior && pet.pet_behavior !== pet.pet_notes && <div className="text-[11px] text-velta-mute mt-2">Comportamento: {pet.pet_behavior}</div>}
          </div>
          <button onClick={onClose} data-testid="pet-history-close" className="text-velta-mute hover:text-velta-ink"><X className="w-5 h-5" /></button>
        </div>

        {stats && (
          <div className="grid grid-cols-4 gap-px bg-velta-border border-b border-velta-border" data-testid="pet-history-stats">
            <div className="bg-white p-3 text-center">
              <div className="font-mono text-xl font-bold">{stats.visits}</div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-velta-mute">Visite</div>
            </div>
            <div className="bg-white p-3 text-center">
              <div className="font-mono text-xl font-bold text-velta-success">{stats.completed_visits}</div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-velta-mute">Completate</div>
            </div>
            <div className="bg-white p-3 text-center">
              <div className="font-mono text-xl font-bold">{formatEUR(stats.total_spent)}</div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-velta-mute">Speso</div>
            </div>
            <div className="bg-white p-3 text-center">
              <div className="font-mono text-xl font-bold">{stats.photos_count}</div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-velta-mute">Foto</div>
            </div>
          </div>
        )}

        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {error && <p className="text-sm text-velta-error" data-testid="pet-history-error">{error}</p>}
          {!data && !error && <p className="text-sm text-velta-mute">Caricamento…</p>}
          {(Array.isArray(data?.appointments) ? data?.appointments : []).map((a) => {
            const s = STATUS_BADGES[a.status] || STATUS_BADGES.scheduled;
            const total = (a.services || []).reduce((sum, sv) => sum + (sv.price || 0), 0) || a.service_price || 0;
            return (
              <div key={a.id} data-testid={`pet-history-appt-${a.id}`} className="border border-velta-border rounded-md p-3">
                <div className="flex items-center justify-between gap-2">
                  <div className="text-sm font-semibold">{fmtDT(a.start_at)}</div>
                  <span className="text-[9px] font-mono uppercase font-bold px-1.5 py-0.5 rounded text-white" style={{ background: s.bg }}>{s.text}</span>
                </div>
                {(a.services || []).length > 0 && (
                  <div className="text-xs text-velta-ink mt-1.5 flex flex-wrap gap-1.5">
                    {(Array.isArray(a.services) ? a.services : []).map((sv, i) => (
                      <span key={i} className="border border-velta-border rounded px-1.5 py-0.5">{sv.name} · {formatEUR(sv.price)}</span>
                    ))}
                  </div>
                )}
                {total > 0 && <div className="text-[11px] font-mono text-velta-mute mt-1">Totale servizi: {formatEUR(total)}</div>}
                {a.notes && <div className="text-[11px] text-velta-mute mt-1 italic">{a.notes}</div>}
                {(a.photos || []).length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2 pt-2 border-t border-velta-border/60">
                    {(Array.isArray(a.photos) ? a.photos : []).map((p) => (
                      <button
                        key={p.id}
                        data-testid={`pet-history-photo-${p.id}`}
                        onClick={() => openPhoto(a.id, p.id)}
                        className="text-[10px] font-mono inline-flex items-center gap-1 border border-velta-border rounded px-2 py-1 hover:border-velta-ink transition"
                      >
                        <Camera className="w-3 h-3" /> {PHOTO_TYPES[p.type] || p.type}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {lightbox && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center p-6 z-[60]" onClick={(e) => { e.stopPropagation(); setLightbox(null); }}>
          <div className="max-w-2xl w-full" data-testid="pet-photo-lightbox">
            <img src={lightbox.data_url} alt={PHOTO_TYPES[lightbox.type] || "foto"} className="w-full max-h-[80vh] object-contain rounded-md" />
            <div className="text-white text-center text-sm mt-2 font-mono">
              {PHOTO_TYPES[lightbox.type] || lightbox.type}{lightbox.note ? ` — ${lightbox.note}` : ""} · tocca per chiudere
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
